import { useState, useEffect, useCallback } from 'react';
import { getShopRequests, acceptOrder } from '../utils/api';

const SERVICE_ICONS = {
  washing: '🫧', dry_cleaning: '👔', laundry: '🧺', ironing: '♨️'
};

function RequestCard({ order, onAccepted }) {
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await acceptOrder(order.id);
      setAccepted(true);
      onAccepted?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to accept order');
      setAccepting(false);
    }
  };

  return (
    <div className="card" style={{ opacity: accepted ? 0.6 : 1, transition: 'opacity 0.3s' }}>
      <div style={{ padding: '1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700 }}>Order #{order.id?.slice(-8)}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 2 }}>
            {new Date(order.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <span className="badge badge-pending">New Request</span>
      </div>

      <div style={{ padding: '1rem 1.25rem' }}>
        {/* Customer info */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, padding: '10px 12px', background: 'var(--bg)', borderRadius: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>👤</div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{order.customer?.name || 'Customer'}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{order.customer?.phone}</div>
          </div>
        </div>

        {/* Order details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 10 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Clothes</div>
            <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>{order.clothes_count} <span style={{ fontSize: '0.85rem', fontWeight: 400 }}>items</span></div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 10 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Services</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {order.services?.map(s => (
                <span key={s} style={{ fontSize: '1rem' }}>{SERVICE_ICONS[s] || '🧴'}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Services list */}
        <div className="order-services" style={{ marginBottom: 12 }}>
          {order.services?.map(s => (
            <span key={s} className="order-service-tag">{s.replace('_', ' ')}</span>
          ))}
        </div>

        {/* Address */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16, padding: '10px 12px', background: '#fff7ed', borderRadius: 10, fontSize: '0.85rem' }}>
          <span>📍</span>
          <span>{order.customer_address}</span>
        </div>

        {order.notes && (
          <div style={{ padding: '8px 12px', background: 'var(--primary-light)', borderRadius: 8, fontSize: '0.83rem', color: 'var(--primary-dark)', marginBottom: 12 }}>
            💬 {order.notes}
          </div>
        )}

        {/* Accept button */}
        {!accepted ? (
          <button
            className="btn btn-success btn-full"
            onClick={handleAccept}
            disabled={accepting}
            style={{ fontSize: '1rem', padding: '12px' }}
          >
            {accepting ? '⏳ Accepting...' : '✅ Accept Order'}
          </button>
        ) : (
          <div style={{ textAlign: 'center', padding: '12px', background: '#d1fae5', borderRadius: 10, color: '#065f46', fontWeight: 600 }}>
            ✅ Order Accepted!
          </div>
        )}
      </div>
    </div>
  );
}

export default function OwnerRequestsPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      const res = await getShopRequests();
      setOrders(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      if (e.response?.status !== 404) console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    // Poll every 15 seconds for new orders
    const interval = setInterval(loadRequests, 15000);
    return () => clearInterval(interval);
  }, [loadRequests]);

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Incoming Requests</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: '0.85rem' }}>
            Auto-refreshes every 15s · {lastRefresh ? `Last: ${lastRefresh.toLocaleTimeString()}` : ''}
          </p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={loadRequests}>
          🔄 Refresh
        </button>
      </div>

      {loading && <div className="spinner" />}

      {!loading && orders.length === 0 && (
        <div style={{ textAlign: 'center', padding: '5rem 2rem', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>📭</div>
          <h3 style={{ marginBottom: 8 }}>No pending requests</h3>
          <p>New customer orders will appear here automatically.</p>
          <div style={{ marginTop: 16, padding: '12px 20px', background: 'var(--bg)', borderRadius: 12, display: 'inline-block', fontSize: '0.85rem' }}>
            Make sure your shop is set up at <strong>My Shop</strong> tab
          </div>
        </div>
      )}

      <div className="grid-auto">
        {orders.map(order => (
          <RequestCard key={order.id} order={order} onAccepted={loadRequests} />
        ))}
      </div>
    </div>
  );
}
